import { KeyboardArrowDown } from '@mui/icons-material';
import {
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    SelectProps,
    Box,
    alpha,
} from '@mui/material';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

interface SelectOption {
    value: any;
    label: React.ReactNode;
    disabled?: boolean;
    iconRight?: React.ReactNode;
    iconLeft?: React.ReactNode;
    dividerTop?: boolean;
    groupLabel?: string;
    badgeLabel?: string;
    badgeVariant?: 'filled' | 'outlined';
    groupLayout?: 'row' | 'grid';
}

interface FormSelectProps extends SelectProps {
    name: string;
    options: SelectOption[];
    control: Control<any>;
    label?: string;
    fontSize?: string;
    groupedMenu?: boolean;
}

/**
 * For use within RHF FormProvider
 */
const FormSelect = ({
    name,
    control,
    options = [],
    label,
    fontSize,
    groupedMenu = false,
    onChange: externalOnChange,
    labelId: labelIdProp,
    ...props
}: FormSelectProps) => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const labelId = labelIdProp ?? `select-label-${name}`;

    return (
        <Controller
            control={control}
            name={name}
            render={({
                field: { onChange: fieldOnChange, value, ref },
                fieldState: { error },
            }) => (
                <FormControl fullWidth size='small'>
                    <InputLabel
                        sx={{
                            ...(fontSize ? { fontSize } : {}),
                        }}
                        id={labelId}
                    >
                        {label}
                    </InputLabel>

                    <Select
                        {...props}
                        labelId={labelId}
                        inputRef={ref}
                        size='small'
                        open={menuOpen}
                        onOpen={() => setMenuOpen(true)}
                        onClose={() => setMenuOpen(false)}
                        onChange={(e, child) => {
                            if (externalOnChange) {
                                externalOnChange(e as any, child as any);
                            }
                            fieldOnChange(e);
                        }}
                        //This prevents passing undefined values to FormSelect
                        value={value ?? (props.multiple ? [] : '')}
                        fullWidth
                        error={!!error}
                        label={label}
                        IconComponent={KeyboardArrowDown}
                        sx={{ ...(fontSize ? { fontSize } : {}) }}
                        MenuProps={
                            groupedMenu
                                ? {
                                      PaperProps: {
                                          sx: {
                                              '& .MuiList-root': {
                                                  p: 0,
                                                  display: 'block',
                                              },
                                          },
                                      },
                                  }
                                : undefined
                        }
                        renderValue={(selected) => {
                            const selectedOption = options.find(
                                (o) => o.value === selected,
                            );

                            if (!selectedOption) return selected;

                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                    }}
                                >
                                    {selectedOption.iconLeft}
                                    {selectedOption.label}
                                </Box>
                            );
                        }}
                    >
                        {options.map((option, index) => {
                            const prev = options[index - 1];
                            const groupStartIndex = option.groupLabel
                                ? options.findIndex(
                                      (o) => o.groupLabel === option.groupLabel,
                                  )
                                : -1;
                            const groupItemIndex =
                                groupStartIndex >= 0
                                    ? index - groupStartIndex
                                    : 0;
                            const showGroupHeader =
                                groupedMenu &&
                                option.groupLabel &&
                                option.groupLabel !== prev?.groupLabel;

                            return [
                                showGroupHeader ? (
                                    <ListSubheader
                                        key={`header-${option.value}`}
                                        disableSticky
                                        sx={{
                                            lineHeight: '34px',
                                            fontWeight: 900,
                                            letterSpacing: '0.08em',
                                            fontSize: '0.72rem',
                                            textTransform: 'uppercase',
                                            color: 'text.primary',
                                            px: 1.5,
                                            py: 0.25,
                                            bgcolor: (t) =>
                                                alpha(
                                                    t.palette.primary.main,
                                                    0.06,
                                                ),
                                            borderBottom: '1px solid',
                                            borderBottomColor: 'divider',
                                            borderTop:
                                                index === 0
                                                    ? 'none'
                                                    : '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        {option.groupLabel ===
                                        'BOOK VIA 7DAY' ? (
                                            <Box
                                                component='span'
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'baseline',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Box component='span'>
                                                    {option.groupLabel}
                                                </Box>
                                                <Box
                                                    component='span'
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '0.68rem',
                                                        letterSpacing: '0.02em',
                                                        textTransform: 'none',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    (recommended)
                                                </Box>
                                            </Box>
                                        ) : (
                                            option.groupLabel
                                        )}
                                    </ListSubheader>
                                ) : null,
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disabled}
                                    onClick={() => {
                                        if (option.disabled) return;
                                        fieldOnChange(option.value);
                                        setMenuOpen(false);
                                    }}
                                    sx={{
                                        ...(option.dividerTop
                                            ? {
                                                  borderTop: 1,
                                                  borderColor: 'divider',
                                                  mt: 0.5,
                                              }
                                            : {}),
                                        ...(groupedMenu
                                            ? option.groupLayout === 'grid'
                                                ? {
                                                      width: {
                                                          xs: '100%',
                                                          sm: '50%',
                                                      },
                                                      display: 'inline-flex',
                                                      boxSizing: 'border-box',
                                                      borderBottom: '1px solid',
                                                      borderColor: 'divider',
                                                      verticalAlign: 'top',
                                                      borderRight:
                                                          groupItemIndex % 2 ===
                                                          0
                                                              ? {
                                                                    sm: '1px solid',
                                                                }
                                                              : undefined,
                                                  }
                                                : {
                                                      width: '100%',
                                                      boxSizing: 'border-box',
                                                      borderBottom: '1px solid',
                                                      borderColor: 'divider',
                                                  }
                                            : {}),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                            }}
                                        >
                                            {option.iconLeft} {option.label}
                                        </Box>
                                        {option.badgeLabel ? (
                                            <Chip
                                                size='small'
                                                label={option.badgeLabel}
                                                variant={
                                                    option.badgeVariant ??
                                                    'filled'
                                                }
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.66rem',
                                                    fontWeight: 700,
                                                    flexShrink: 0,
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        ) : (
                                            option.iconRight
                                        )}
                                    </Box>
                                </MenuItem>,
                            ];
                        })}
                    </Select>

                    {!!error && (
                        <FormHelperText error>{error.message}</FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
};

export default FormSelect;
